import { useState, type CSSProperties } from "react";
import type { World } from "../sendItEngine";
import { APP_REQUESTS, type AppRequest } from "./content";
import { Ic } from "./icons";
import { tx } from "../../lib/tx";

export type AppStatus = "pending" | "approved" | "removed" | "declined" | "reported";

export function appStatus(world: World, cancelled: string[], id: string): AppStatus {
  const grants = world.apps.filter((a) => a.appId === id);
  if (grants.some((a) => !a.revoked)) return "approved";
  if (world.reported.includes(id)) return "reported";
  if (grants.some((a) => a.revoked)) return "removed";
  if (cancelled.includes(id)) return "declined";
  return "pending";
}

const STATUS_TEXT: Record<AppStatus, string> = {
  pending: "Waiting for your decision",
  approved: "Approved",
  removed: "Access removed",
  declined: "Declined",
  reported: "Declined and reported",
};

function Card({
  app,
  status,
  index,
  onApprove,
  onCancel,
  onRemove,
  onReport,
}: {
  app: AppRequest;
  status: AppStatus;
  index: number;
  onApprove: () => void;
  onCancel: (report: boolean) => void;
  onRemove: () => void;
  onReport: () => void;
}) {
  const [again, setAgain] = useState(false);
  const decided = status !== "pending";
  const showChoices = !decided || again;
  return (
    <article className={`sis-app is-${status}`} style={{ "--i": index } as CSSProperties}>
      <header className="sis-app-h">
        <span className="sis-app-tile" style={{ background: app.tint }} aria-hidden="true">
          <Ic n="plug" size={20} />
        </span>
        <div className="sis-app-id">
          <strong>{app.name}</strong>
          <span>{app.publisher}</span>
        </div>
        <span className={`sis-status is-${status}`} key={status}>
          {status === "approved" && <Ic n="check" size={12} />}
          {STATUS_TEXT[status]}
        </span>
      </header>
      <p className="sis-app-blurb">{app.blurb}</p>
      <div className="sis-app-perm">
        <span className="sis-app-ph">{tx("This app wants to")}</span>
        <ul>
          {app.permissions.map((p) => (
            <li key={p}>
              <Ic n="lock" size={12} /> {p}
            </li>
          ))}
        </ul>
      </div>
      <div className="sis-app-act">
        {showChoices ? (
          <>
            <button
              type="button"
              className="sis-btn sis-btn--primary sis-btn--sm"
              onClick={() => {
                setAgain(false);
                onApprove();
              }}
            >
              {tx("Approve")}
            </button>
            <button
              type="button"
              className="sis-btn sis-btn--sm"
              onClick={() => {
                setAgain(false);
                onCancel(false);
              }}
            >
              {tx("Cancel")}
            </button>
            <button
              type="button"
              className="sis-btn sis-btn--sm"
              onClick={() => {
                setAgain(false);
                onCancel(true);
              }}
            >
              {tx("Cancel and report")}
            </button>
          </>
        ) : status === "approved" ? (
          <>
            <button type="button" className="sis-btn sis-btn--sm sis-btn--danger" onClick={onRemove}>
              {tx("Remove access")}
            </button>
            <button type="button" className="sis-btn sis-btn--sm" onClick={onReport}>
              {tx("Report this app")}
            </button>
          </>
        ) : (
          <button type="button" className="sis-btn sis-btn--sm" onClick={() => setAgain(true)}>
            {tx("Review again")}
          </button>
        )}
      </div>
    </article>
  );
}

/** Connected apps: permission requests from document tools. Approving gives the app exactly what it lists. */
export function AppsWin({
  world,
  cancelled,
  onApprove,
  onCancel,
  onRemove,
  onReport,
  onGuide,
}: {
  world: World;
  cancelled: string[];
  onApprove: (id: AppRequest["id"]) => void;
  onCancel: (id: AppRequest["id"], report: boolean) => void;
  onRemove: (id: AppRequest["id"]) => void;
  onReport: (id: AppRequest["id"]) => void;
  onGuide: () => void;
}) {
  return (
    <div className="sis-apps">
      <div className="sis-apps-intro">
        <span className="sis-apps-ic"><Ic n="shield" size={20} /></span>
        <div>
          <strong>{tx("Apps are asking to connect")}</strong>
          <span>{tx("Approving gives an app everything it lists, until you remove it. Read each request before you decide.")}</span>
        </div>
        <button type="button" className="sis-link" onClick={onGuide}>
          <Ic n="book" size={13} /> {tx("Open the Sharing Guide")}
        </button>
      </div>
      <div className="sis-apps-grid sis-scroll">
        {APP_REQUESTS.map((app, i) => (
          <Card
            key={app.id}
            app={app}
            index={i}
            status={appStatus(world, cancelled, app.id)}
            onApprove={() => onApprove(app.id)}
            onCancel={(report) => onCancel(app.id, report)}
            onRemove={() => onRemove(app.id)}
            onReport={() => onReport(app.id)}
          />
        ))}
      </div>
    </div>
  );
}
